// NOMBASE - Thin wrapper over react-query for our fetch-based API layer.
// Most existing pages use `fetch` directly; this lets us migrate incrementally
// without forcing a rewrite to the `api` service object.
//
// All requests route through `apiRequest` (services/api.js) which provides:
//   • In-flight GET deduplication (multiple components hitting the same
//     endpoint in the same tick share one network call)
//   • 5xx circuit breaker (stops piling on a struggling backend)
//   • 15s hard timeout via AbortController
//   • Self-diagnosing 500 surfacing (error_class + request_id in tag)
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/services/api';

// GET a URL path (e.g. `/api/dashboard/brand`) and cache it by the path.
// `enabled` lets callers delay until prerequisites are ready.
export function useApiQuery(path, { enabled = true, key, ...opts } = {}) {
  return useQuery({
    queryKey: key || ['api', path],
    enabled: enabled && !!path,
    queryFn: async () => {
      // apiRequest expects an endpoint without the leading `/api`
      // (it inserts it). Existing callers pass `/api/dashboard/brand`,
      // so strip the prefix once here.
      const endpoint = path.startsWith('/api') ? path.slice(4) : path;
      try {
        return await apiRequest(endpoint);
      } catch (e) {
        // apiRequest already throws an Error with the backend's
        // self-diagnosing fields embedded in the message when present.
        // T42 — capture to Sentry so a 5xx on a critical path
        // (dashboards, requests, scorecards) becomes an actionable
        // event instead of disappearing into a useQuery `error` field
        // that the page may or may not surface visually.
        try {
          // Lazy import — Sentry is optional in dev.
          const Sentry = (await import('@sentry/react'));
          Sentry.captureException?.(e, {
            tags: { useApiQuery_path: path },
          });
        } catch { /* Sentry not initialised */ }
        // Re-throw so React Query's error state is populated.
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    ...opts,
  });
}

// For mutations that should bust cached GETs, pass the path(s) to invalidate.
export { useMutation } from '@tanstack/react-query';
